import React from "react";
import "./PrivacyPolicy.scss";

const PrivacyPolicy: React.FC = () => {
  return (
    <main id="privacy-policy" className="privacy-policy">
      <h1>Privacy Policy</h1>
      <p className="effective-date">Effective date: May 7, 2026</p>

      <h2>Introduction</h2>
      <p>
        Mindara ("we", "our", or "us") operates the Mindara iOS application and
        the mindara.io website. This Privacy Policy explains what data we
        collect, how we use it, and your rights regarding your personal
        information.
      </p>

      <h2>Information We Collect</h2>
      <p>
        We collect the following categories of information when you use Mindara:
      </p>
      <ul>
        <li>
          <strong>Email address and name</strong> — collected during account
          creation via Sign in with Apple or Sign in with Google. Used to
          identify your account and personalize your experience.
        </li>
        <li>
          <strong>Coarse location</strong> — collected when you grant location
          permission. Used to show nearby venues and improve place
          recommendations. We do not collect precise GPS coordinates beyond what
          is needed for local search.
        </li>
        <li>
          <strong>Purchase history</strong> — records of subscription purchases
          made through Apple In-App Purchase. Used to manage your Mindara
          Premium subscription status.
        </li>
        <li>
          <strong>Device identifier</strong> — a non-resettable device
          identifier used for analytics and crash reporting. Linked to your
          account to help us diagnose issues.
        </li>
        <li>
          <strong>Crash data</strong> — diagnostic information generated when
          the app crashes, collected via Firebase Crashlytics. Used solely to
          identify and fix software bugs.
        </li>
        <li>
          <strong>Product interaction data</strong> — how you interact with app
          features (screens visited, features used, session duration), collected
          via Firebase Analytics. Used to understand how Mindara is used and to
          improve the product.
        </li>
      </ul>

      <h2>How We Use Your Information</h2>
      <ul>
        <li>To create and manage your Mindara account</li>
        <li>
          To provide personalized venue recommendations based on your taste
          profile and location
        </li>
        <li>To process subscription transactions via Apple In-App Purchase</li>
        <li>To diagnose crashes and fix bugs (crash data)</li>
        <li>
          To understand how users interact with features so we can improve the
          app (analytics)
        </li>
        <li>
          To send push notifications for plan updates and friend RSVPs (only
          with your permission)
        </li>
      </ul>

      <h2>Firebase Analytics and Tracking</h2>
      <p>
        Mindara uses Firebase Analytics, which operates under the domain{" "}
        <code>firebaseanalytics.com</code>. Firebase Analytics collects product
        interaction and device identifier data. This data is used for app
        analytics only — we do not use Firebase Analytics to track you across
        third-party apps or websites. Our NSPrivacyTracking flag is set to{" "}
        <code>false</code> in our App Store privacy manifest.
      </p>

      <h2>Data Sharing</h2>
      <p>We do not sell your personal data. We share data only with:</p>
      <ul>
        <li>
          <strong>Google Firebase / Crashlytics</strong> — for analytics and
          crash reporting, under Google's data processing terms.
        </li>
        <li>
          <strong>Resy</strong> — when you make a restaurant reservation through
          the app, we share the booking details required to complete the
          reservation.
        </li>
        <li>
          <strong>Apple</strong> — subscription purchase data is processed by
          Apple under their App Store terms.
        </li>
      </ul>

      <h2>Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>
          <strong>Delete your account and all associated data</strong> — go to
          Settings → Account → Delete Account in the Mindara iOS app. Deletion
          is permanent and processed within 30 days.
        </li>
        <li>
          <strong>Request a copy of your data</strong> — email us at
          privacy@mindara.io.
        </li>
        <li>
          <strong>Opt out of analytics</strong> — on iOS, use Settings → Privacy
          & Security → Tracking to limit app tracking, or contact us to request
          analytics opt-out.
        </li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        We retain your account data for as long as your account is active. When
        you delete your account, we permanently delete your profile, plans, and
        personal data within 30 days. Crash logs and aggregated analytics data
        may be retained for up to 12 months in anonymized form.
      </p>

      <h2>Children's Privacy</h2>
      <p>
        Mindara is not directed at children under 13. We do not knowingly
        collect personal information from children under 13. If you believe a
        child under 13 has provided us personal data, contact us at
        privacy@mindara.io and we will delete it promptly.
      </p>

      <h2>Contact</h2>
      <p>
        For privacy questions or data requests, contact us at:{" "}
        <a href="mailto:privacy@mindara.io">privacy@mindara.io</a>
      </p>

      <p className="last-updated">Last updated: May 7, 2026</p>
    </main>
  );
};

export default PrivacyPolicy;
